/**
 * Which numbers in the scene are lengths (canvas pixels) and which are not, per element type and per animatable
 * property. Changing a graphic's format scales exactly the lengths (the editor's "Change format…", ADR 0036); a unit
 * test walks the scene's JSON Schema and fails when a numeric property is in neither list, so a new property cannot
 * be missed. Paths are dotted property names from the element; arrays (gradient stops, the four corner radii) are
 * walked through, and a path that an element does not have is skipped.
 */
import type { AnimatableProperty, Element } from './scene.ts';

export interface ElementNumbers {
  /** Canvas pixels: scaled with the content. */
  lengths: readonly string[];
  /** Ratios, angles, percentages, weights, counts and times: unchanged. */
  other: readonly string[];
}

const base: ElementNumbers = {
  lengths: ['transform.x', 'transform.y', 'size.width', 'size.height', 'size.minWidth', 'size.maxWidth', 'size.minHeight', 'size.maxHeight'],
  other: ['transform.rotation', 'transform.scaleX', 'transform.scaleY', 'transform.originX', 'transform.originY', 'transform.opacity'],
};

const shapeStyle: ElementNumbers = {
  lengths: ['style.stroke.width', 'style.cornerRadius', 'style.shadow.x', 'style.shadow.y', 'style.shadow.blur', 'style.blur'],
  other: ['style.fill.angle', 'style.fill.stops.offset'],
};

const join = (...parts: ElementNumbers[]): ElementNumbers => ({ lengths: parts.flatMap((p) => p.lengths), other: parts.flatMap((p) => p.other) });

export const ELEMENT_NUMBERS: { readonly [K in Element['type']]: ElementNumbers } = {
  group: join(base, shapeStyle, {
    lengths: ['layout.stack.gap', 'layout.padding.top', 'layout.padding.right', 'layout.padding.bottom', 'layout.padding.left'],
    other: [],
  }),
  clipMask: join(base, { lengths: ['cornerRadius'], other: [] }),
  text: join(base, {
    lengths: ['style.fontSize', 'style.letterSpacing', 'style.stroke.width', 'style.shadow.x', 'style.shadow.y', 'style.shadow.blur', 'overflow.minFontSize'],
    // lineHeight is a multiple of the font size.
    other: ['style.fontWeight', 'style.lineHeight', 'style.fill.angle', 'style.fill.stops.offset', 'overflow.maxLines'],
  }),
  rect: join(base, shapeStyle),
  ellipse: join(base, { lengths: shapeStyle.lengths.filter((p) => p !== 'style.cornerRadius'), other: shapeStyle.other }),
  line: join(base, { lengths: ['stroke.width'], other: [] }),
  image: base,
  video: base,
};

/** Animatable properties whose keyframe values (absolute, and `{ rel }` offsets) are lengths. */
export const LENGTH_TRACK_PROPERTIES: readonly AnimatableProperty[] = ['x', 'y', 'width', 'height', 'blur', 'letterSpacing'];
/** The rest: scale factors, degrees, opacity, inset percentages and colours. */
export const OTHER_TRACK_PROPERTIES: readonly AnimatableProperty[] = ['scaleX', 'scaleY', 'rotation', 'opacity', 'insetTop', 'insetRight', 'insetBottom', 'insetLeft', 'color'];
